/**
 * The live consumption estimate — what this set costs in leather, computed from
 * what is on the screen right now.
 *
 * The point of it is the word LIVE. Everything else on the review screen is
 * about getting the geometry right; this is the number the model room actually
 * argues about, and until now it could only be seen after a save, an approval
 * and a trip to another screen. Here it moves while the operator is still
 * labelling: change a material, change a quantity, delete the paper label that
 * traced as a part, and the desi per pair moves with it.
 *
 * Three figures per material, because they answer three different questions:
 *
 *   net desi/çift      the area of the pieces themselves, quantities applied.
 *                      The floor — no layout can do better.
 *   ölçek desi/çift    Σ of the Russ & Small parallelogram per piece
 *                      (`scaleAreaAssess`), i.e. what one piece consumes in an
 *                      infinite lattice of ITSELF at its own direction rule.
 *                      The classic footwear assessment, and per-piece cacheable.
 *   tüketim desi/çift  the whole set laid on one strip (`stripPlaceBest`, the
 *                      bottom-left placer over its small fixed set of feeding
 *                      orders), which is the only figure that accounts for
 *                      pieces nesting into EACH OTHER rather than into copies
 *                      of themselves.
 *   zarf               one pair nested freely (`envelopePlace`, no imposed
 *                      strip width) and read as its tight bounding envelope —
 *                      the "one çift of this material needs X desi" figure,
 *                      shipped as `MaterialEstimate.envelope`.
 *
 * It is an ESTIMATE and it says so on the card. Two knobs are deliberately
 * coarser than the library's defaults — a 2 mm assessment grid and a 15° angle
 * step — because a number that arrives while you are typing is worth more than
 * the last two percent of a number that arrives in five seconds. Measured
 * against the 1 mm / 5° settings on a real set the ölçek figure moves by ~1,5 %
 * and the strip length by ~3 %, both in the pessimistic direction. When a set is
 * costed for real, the server re-derives it from this same library at the
 * library's own defaults.
 *
 * Pure by construction: no DOM, no React, no fetch. That is what lets it run in
 * a web worker (`live-calc.worker.ts`) AND be unit tested in plain Node.
 */

import {
  decimateRing,
  DegenerateInstanceError,
  envelopePlace,
  mirrorX,
  scaleAreaAssess,
  stripPlaceBest,
  StripWidthError,
  type Part,
  type StripOrdering,
} from '../../../worker/lib/yield'
import {
  mm2ToDesi,
  type Handedness,
  type Orientation,
  type PartGeom,
  type PurchaseUnit,
} from '../../../worker/api/saya.contract'

/* ── Assessment settings ────────────────────────────────────────────────────── */

/**
 * The strip everything is laid on, mm.
 *
 * For roll and sheet goods the material's own width would be the honest figure;
 * this card deliberately uses ONE width for every material so the numbers on it
 * are comparable to each other. It is the leather "değerlendirme genişliği" the
 * design names — an in-house convention, not a simulation of a bought hide.
 */
export const LIVE_STRIP_WIDTH_MM = 300

/** Assessment grid for the live card. Coarser than the library default; see above. */
export const LIVE_CELL_MM = 2

/** Angle step for a `serbest` piece on the live card. */
export const LIVE_ANGLE_STEP_DEG = 15

/** Rotations the strip placer may use for a piece with no direction rule. */
export const LIVE_ROTATIONS_DEG: number[] = Array.from({ length: 24 }, (_, index) => index * 15)

/** A `tek_yon` piece is laid one way up and no other. */
export const LIVE_ONE_WAY_ROTATIONS_DEG: number[] = [0]

/** A `cift_yon` piece may also be turned end for end, and nothing between. */
export const LIVE_TWO_WAY_ROTATIONS_DEG: number[] = [0, 180]

/**
 * The rotations a whole material's strip run may use.
 *
 * `stripPlace` takes ONE rotation list for the layout, not one per instance, so
 * the group is placed under the strictest rule anybody in it carries. That is
 * the right shape for this card rather than a compromise: `tek_yon`/`cift_yon`
 * is a property of the material's grain or nap, the group IS one material, and
 * a suede piece next to a free one on the same hide does not make the hide
 * directionless.
 *
 * Without this the consumption figure — the headline number on the card —
 * quietly assumed every piece could be spun to any of 24 angles, which flatters
 * a napped leather layout by exactly the amount the direction rule exists to
 * pay for. `scaleAreaAssess` has always honoured the rule per piece; this is the
 * other figure catching up.
 */
export function stripRotationsFor(pieces: readonly EstimatePiece[]): number[] {
  if (pieces.some((piece) => piece.orientation === 'tek_yon')) return LIVE_ONE_WAY_ROTATIONS_DEG
  if (pieces.some((piece) => piece.orientation === 'cift_yon')) return LIVE_TWO_WAY_ROTATIONS_DEG
  return LIVE_ROTATIONS_DEG
}

/**
 * How many times the pair is laid down. Two, so a piece can nest into the NEXT
 * pair's pieces — which is what a cutter does — without paying for a long run
 * nobody would wait for.
 */
export const LIVE_REPEATS = 2

/**
 * The feeding orders the live strip run lets `stripPlaceBest` try — three of
 * the library's four, 'tallest' dropped. Measured on saya-sized sets at the
 * card's own knobs (2 mm cells, 24 rotations, 300 mm strip): the full set
 * pushes a heavy-but-plausible material (36 instances) past ~2 s in the
 * worker while these three stay under, and across the placement fixture
 * family this is the subset that gives up the least against all four —
 * 'tallest' almost never wins alone. The server's own costing run is free to
 * use the library default.
 */
export const LIVE_STRIP_ORDERINGS: readonly StripOrdering[] = [
  'as-given',
  'diagonal',
  'interleave',
]

/**
 * Leather is cut from a hide with a natural edge, holes and brand marks, so the
 * usable fraction is never all of it. 5 % is the allowance the factory quotes;
 * it is applied ONLY to leather, because roll and sheet goods have no such loss.
 */
export const LEATHER_YIELD_FACTOR = 0.95

/**
 * Above this many instances the strip and envelope placements are skipped
 * rather than left to run for a minute. A saya set is ten to twenty pieces;
 * anything that reaches this is a mis-trace, and a card that freezes is worse
 * than a card that says why it is empty.
 */
export const LIVE_MAX_INSTANCES = 120

/* ── Wire shapes (main thread ⇄ worker) ─────────────────────────────────────── */

export interface EstimatePiece {
  /** Stable identity of the shape — geometry only, see `geomKey`. */
  geomKey: string
  materialId: number | null
  qtyPerPair: number
  handedness: Handedness
  orientation: Orientation
  netMm2: number
  geom: PartGeom
}

export interface EstimateMaterial {
  id: number
  name: string
  gapMm: number
  /** `desi` is leather, and leather is the only thing that carries a fire pay. */
  purchaseUnit: PurchaseUnit
}

export interface EstimateRequest {
  pieces: EstimatePiece[]
  materials: EstimateMaterial[]
}

/**
 * One piece's ölçek lattice, ready to draw: the parallelogram cell, the angle
 * the piece lies at, and — for a k = 2 result — where its 180° mate sits.
 * A verbatim slice of `ScaleAreaResult`, minus the fields the viz never reads.
 */
export interface PieceScale {
  perPieceMm2: number
  efficiency: number
  k: 1 | 2
  angleDeg: number
  u: [number, number]
  v: [number, number]
  mateOffsetMm: [number, number] | null
}

/** One entry of `MaterialLayout.instances`: which piece, which chirality, its outline. */
export interface LayoutInstance {
  /** Index into the REQUEST's `pieces` array — the set-wide piece identity. */
  pieceIndex: number
  /** True on every second copy of an `aynali_cift` piece — the mirrored mate. */
  mirrored: boolean
  /** Decimated outer ring, `[x0, y0, x1, y1, …]` mm, implicitly closed. */
  outlineMm: Float32Array
}

/** One placed instance on the strip, in the strip's own mm frame. */
export interface LayoutPlacement {
  /** Translation applied AFTER `rotDeg`, mm. */
  xMm: number
  yMm: number
  /** Rotation applied to the outline before translating, degrees CCW. */
  rotDeg: number
  /** Index into `MaterialLayout.instances`. */
  instance: number
}

/**
 * Everything the yield visualisation needs to redraw one material's strip and
 * its pieces' ölçek lattices — shipped FROM the worker so the geometry drawn
 * is guaranteed to be what the placer saw, decimated to 0,5 mm for the wire.
 *
 * INSTANCE ORDER IS THE CONTRACT: biggest net area first (the order `stripFor`
 * hands the placer, see `orderedForStrip`), each piece expanded to its
 * per-pair copies with every second copy mirrored under `aynali_cift` (see
 * `instancesOf`). `stripPlaceBest` may internally feed a variant order, but
 * its placements always come back indexed into — and per pass ordered by —
 * the array it was handed. `placements[i].instance` indexes into that array;
 * `placements` covers `LIVE_REPEATS` passes over it, so the drawn strip holds
 * that many pairs.
 */
export interface MaterialLayout {
  stripWidthMm: number
  usedLengthMm: number
  /** `Σ A_net(placed) / gross` — identical to the card's verim by construction. */
  efficiency: number
  placements: LayoutPlacement[]
  instances: LayoutInstance[]
  /** Keyed by `pieceScaleKey` — shape + orientation, the lattice's identity. */
  scale: Record<string, PieceScale>
}

/**
 * The zarf (free envelope) figure: ONE PAIR of this material nested freely
 * (`envelopePlace`), reported as the tight bounding box around what was laid —
 * "one çift of this material needs `grossDesi` desi".
 *
 * INSTANCE ORDER IS THE CONTRACT, and it is the SAME contract as
 * `MaterialLayout.instances`: biggest net area first, each piece expanded to
 * its per-pair copies with every second copy mirrored under `aynali_cift`.
 * The envelope is packed at `repeats = 1`, so `instances` is exactly one pass
 * of that ordering — the repeats-1 prefix of what the strip lays repeatedly —
 * and `placements` covers it exactly once: one placement per instance, in
 * instance order. `placements[i].instance` indexes into `instances`.
 *
 * Placements live in the envelope's own mm frame: normalized so the occupied
 * box starts at (0, 0) and ends at (`widthMm`, `lengthMm`).
 */
export interface MaterialEnvelope {
  /** Occupied bounding-box width, mm — the tight envelope, not a strip width. */
  widthMm: number
  /** Occupied bounding-box length, mm. */
  lengthMm: number
  /** `mm2ToDesi(widthMm × lengthMm)` — what one çift of this material needs. */
  grossDesi: number
  /** `Σ A_net / (widthMm × lengthMm)`. */
  efficiency: number
  /**
   * `grossDesi` with the leather fire allowance (`/ LEATHER_YIELD_FACTOR`);
   * null for everything that is not bought in desi — same rule, same home as
   * `wasteInclusiveDesi`: the division lives here, not in the UI.
   */
  fireDesi: number | null
  /**
   * Where the envelope starts in the placements' own frame, `[x, y]` mm — a
   * sub-cell remainder the placer keeps rather than shifting the pieces off
   * its grid (see `EnvelopeResult.originMm`). A drawing frames `originMm` to
   * `originMm + [widthMm, lengthMm]`; assuming (0, 0) puts the frame off by
   * up to one cell.
   */
  originMm: readonly [number, number]
  placements: LayoutPlacement[]
  instances: LayoutInstance[]
}

export interface MaterialEstimate {
  materialId: number
  materialName: string
  /** Pieces on this material, before quantities. */
  pieceCount: number
  /** Instances per pair — quantities applied. */
  instanceCount: number
  netDesi: number
  scaleDesi: number | null
  consumptionDesi: number | null
  /** Consumption with the leather fire allowance; null for everything else. */
  wasteInclusiveDesi: number | null
  /** `net / tüketim`, the number the model room calls verim. */
  efficiency: number | null
  /** Turkish, and only when a figure could not be produced. */
  problem: string | null
  /** The drawable strip + lattices; absent when the placement failed. */
  layout?: MaterialLayout
  /**
   * The zarf figure; absent when no envelope could be computed (no instances,
   * too many instances, or a set the placer refuses everywhere). Independent
   * of `layout`: a piece too wide for the fixed strip can still have an
   * envelope, because the envelope is not bound to `LIVE_STRIP_WIDTH_MM`.
   */
  envelope?: MaterialEnvelope
}

export interface EstimateResult {
  materials: MaterialEstimate[]
  /** Pieces with no material chosen — listed, never added into a total. */
  unassigned: { pieceCount: number; netDesi: number }
  /** Wall-clock cost of this run, for the "hesaplanıyor" state to be honest. */
  elapsedMs: number
}

/** What the screen posts into the worker. `token` is how a stale answer is spotted. */
export interface EstimateJob {
  token: number
  request: EstimateRequest
}

/** What comes back. A failed run answers with `error` rather than dying quietly. */
export interface EstimateAnswer {
  token: number
  result: EstimateResult | null
  error: string | null
}

/* ── Cache ──────────────────────────────────────────────────────────────────── */

/**
 * Results the inputs cannot have changed.
 *
 * The whole request is re-sent on every debounce tick — it is a few hundred
 * kilobytes of numbers and cloning it is free next to the assessment — so the
 * cache is what actually keeps a keystroke cheap: a piece renamed, or a piece
 * whose material moved, does not re-run the lattice search for the eleven
 * pieces that did not move.
 */
export interface EstimateCache {
  /** Full lattice per (shape, orientation, gap); null pins a failed assessment. */
  scale: Map<string, PieceScale | null>
  strip: Map<string, StripOutcome>
  /** Zarf plans, keyed exactly like `strip`; null pins a failed placement. */
  envelope: Map<string, EnvelopePlan | null>
  /**
   * Decimated outlines, one per `geomKey` (plus a `#m` entry for the mirror),
   * so a shape is decimated ONCE for the life of the worker rather than once
   * per keystroke — the layout payload is rebuilt per answer, but it rebuilds
   * from these.
   */
  outlines: Map<string, Float32Array>
}

interface StripOutcome {
  consumptionDesi: number | null
  problem: string | null
  /** The placement as laid, without instance identities — see `stripFor`. */
  layout: StripPlan | null
}

/**
 * The cacheable slice of a layout: geometry only. Instance identities
 * (`pieceIndex`) are deliberately NOT here — the strip cache key does not move
 * when an unrelated piece shifts every request index, so identities are
 * re-derived fresh on every answer while this stays cached.
 */
interface StripPlan {
  usedLengthMm: number
  efficiency: number
  placements: LayoutPlacement[]
}

/**
 * The cacheable slice of a zarf: geometry only, same rule as `StripPlan` —
 * instance identities and the purchase-unit arithmetic (`grossDesi`,
 * `fireDesi`) are re-derived per answer, so the cached plan survives edits
 * that renumber pieces or retag a material's purchase unit.
 */
interface EnvelopePlan {
  widthMm: number
  lengthMm: number
  grossAreaMm2: number
  efficiency: number
  originMm: readonly [number, number]
  placements: LayoutPlacement[]
}

export function createEstimateCache(): EstimateCache {
  return { scale: new Map(), strip: new Map(), envelope: new Map(), outlines: new Map() }
}

/* ── Keys ───────────────────────────────────────────────────────────────────── */

/**
 * A short, stable key for one shape.
 *
 * FNV-1a over the ring coordinates at their stored precision (0,01 mm), with
 * the point count and the net area mixed into the text of the key rather than
 * into the hash — so two different shapes have to collide in the hash AND agree
 * on both of those before a cached number could be handed to the wrong piece.
 */
export function geomKey(geom: PartGeom, netMm2: number): string {
  let hash = 0x811c9dc5
  let points = 0
  for (const ring of [geom.outer, ...geom.holes]) {
    for (const [x, y] of ring) {
      points += 1
      hash = mixNumber(hash, x)
      hash = mixNumber(hash, y)
    }
    hash = mixNumber(hash, -1)
  }
  return `${(hash >>> 0).toString(36)}.${points}.${Math.round(netMm2)}`
}

/**
 * The key `MaterialLayout.scale` is stored under: shape + direction rule. Two
 * rail rows with the same geometry AND the same orientation share one lattice
 * on one material, which is exactly when sharing the record is correct.
 */
export function pieceScaleKey(geomKey: string, orientation: Orientation): string {
  return `${geomKey}|${orientation}`
}

function mixNumber(hash: number, value: number): number {
  let next = hash
  let bits = Math.round(value * 100) | 0
  for (let byte = 0; byte < 4; byte += 1) {
    next ^= bits & 0xff
    next = Math.imul(next, 0x01000193)
    bits >>= 8
  }
  return next
}

/**
 * The signature of a whole request — everything that can move a number, and
 * nothing that cannot.
 *
 * A piece's NAME is deliberately absent: typing in the name box is the most
 * common thing that happens on this screen and it changes no figure on the
 * card, so it must not spend a worker round trip.
 */
export function requestSignature(request: EstimateRequest): string {
  const materials = request.materials
    .map((material) => `${material.id}:${material.gapMm}:${material.purchaseUnit}`)
    .join(',')
  const pieces = request.pieces
    .map(
      (piece) =>
        `${piece.geomKey}:${piece.materialId ?? 'x'}:${piece.qtyPerPair}:${piece.handedness}:${piece.orientation}`,
    )
    .join(',')
  return `${materials}|${pieces}`
}

/* ── The assessment ─────────────────────────────────────────────────────────── */

/**
 * A piece together with its index in the REQUEST's `pieces` array. The index
 * is what a layout's instances point back at — the set-wide identity the UI
 * uses to link a drawn placement to a rail row.
 */
interface IndexedPiece {
  piece: EstimatePiece
  index: number
}

export function computeEstimate(
  request: EstimateRequest,
  cache: EstimateCache = createEstimateCache(),
): EstimateResult {
  const startedAt = Date.now()
  const byMaterial = new Map<number, IndexedPiece[]>()
  const unassigned: EstimatePiece[] = []

  for (let index = 0; index < request.pieces.length; index += 1) {
    const piece = request.pieces[index] as EstimatePiece
    if (piece.materialId == null) {
      unassigned.push(piece)
      continue
    }
    const bucket = byMaterial.get(piece.materialId)
    if (bucket) bucket.push({ piece, index })
    else byMaterial.set(piece.materialId, [{ piece, index }])
  }

  const materials: MaterialEstimate[] = []
  for (const material of request.materials) {
    const pieces = byMaterial.get(material.id)
    if (!pieces || pieces.length === 0) continue
    materials.push(assessMaterial(material, pieces, cache))
  }

  /*
   * A part pointing at a material the payload does not carry: impossible
   * through the rail, but the alternative to handling it is a piece silently
   * missing from every total on the card.
   */
  for (const [materialId, pieces] of byMaterial) {
    if (request.materials.some((material) => material.id === materialId)) continue
    materials.push(
      assessMaterial(
        { id: materialId, name: `Malzeme ${materialId}`, gapMm: 3, purchaseUnit: 'desi' },
        pieces,
        cache,
      ),
    )
  }

  return {
    materials,
    unassigned: {
      pieceCount: unassigned.length,
      netDesi: unassigned.reduce((total, piece) => total + netDesiOf(piece), 0),
    },
    elapsedMs: Date.now() - startedAt,
  }
}

function assessMaterial(
  material: EstimateMaterial,
  indexed: readonly IndexedPiece[],
  cache: EstimateCache,
): MaterialEstimate {
  const netDesi = indexed.reduce((total, { piece }) => total + netDesiOf(piece), 0)
  const instanceCount = indexed.reduce((total, { piece }) => total + quantityOf(piece), 0)

  let scaleDesi: number | null = 0
  let problem: string | null = null
  const scaleRecord: Record<string, PieceScale> = {}
  for (const { piece } of indexed) {
    const cell = scaleAreaFor(piece, material.gapMm, cache)
    if (cell === null) {
      scaleDesi = null
      problem = 'Ölçek alanı bu parçadan hesaplanamadı.'
      break
    }
    scaleDesi += mm2ToDesi(cell.perPieceMm2 * quantityOf(piece))
    scaleRecord[pieceScaleKey(piece.geomKey, piece.orientation)] = cell
  }

  const strip = stripFor(indexed, material.gapMm, instanceCount, cache)
  const consumptionDesi = strip.consumptionDesi
  if (strip.problem) problem = strip.problem

  const zarf = envelopeFor(indexed, material.gapMm, instanceCount, cache)

  /*
   * Instance identities are re-derived HERE, on every answer, rather than
   * cached with the strip: the strip cache key is made of shapes and
   * quantities, so it survives edits that renumber the request's pieces — a
   * cached `pieceIndex` would then point a drawn placement at the wrong rail
   * row. The ordering below is the placer's own, so the indices line up with
   * the cached placements by construction. The envelope shares the SAME
   * identity list: it is packed from the same ordering at `repeats = 1`.
   */
  const identities =
    strip.layout || zarf ? layoutInstances(indexed, cache) : null

  const layout: MaterialLayout | undefined =
    strip.layout && identities
      ? {
          stripWidthMm: LIVE_STRIP_WIDTH_MM,
          usedLengthMm: strip.layout.usedLengthMm,
          efficiency: strip.layout.efficiency,
          placements: strip.layout.placements,
          instances: identities,
          scale: scaleRecord,
        }
      : undefined

  const envelope: MaterialEnvelope | undefined =
    zarf && identities
      ? {
          widthMm: zarf.widthMm,
          lengthMm: zarf.lengthMm,
          grossDesi: mm2ToDesi(zarf.grossAreaMm2),
          efficiency: zarf.efficiency,
          fireDesi:
            material.purchaseUnit === 'desi'
              ? mm2ToDesi(zarf.grossAreaMm2) / LEATHER_YIELD_FACTOR
              : null,
          originMm: zarf.originMm,
          placements: zarf.placements,
          instances: identities,
        }
      : undefined

  return {
    materialId: material.id,
    materialName: material.name,
    pieceCount: indexed.length,
    instanceCount,
    netDesi,
    scaleDesi,
    consumptionDesi,
    wasteInclusiveDesi:
      consumptionDesi != null && material.purchaseUnit === 'desi'
        ? consumptionDesi / LEATHER_YIELD_FACTOR
        : null,
    efficiency: consumptionDesi != null && consumptionDesi > 0 ? netDesi / consumptionDesi : null,
    problem,
    ...(layout ? { layout } : {}),
    ...(envelope ? { envelope } : {}),
  }
}

/** Ölçek lattice of one piece at this material's gap, or null if it failed. */
function scaleAreaFor(
  piece: EstimatePiece,
  gapMm: number,
  cache: EstimateCache,
): PieceScale | null {
  const key = `${piece.geomKey}|${piece.orientation}|${gapMm}`
  if (cache.scale.has(key)) return cache.scale.get(key) ?? null

  try {
    const result = scaleAreaAssess(partOf(piece.geom), {
      orientation: piece.orientation,
      gapMm,
      cellMm: LIVE_CELL_MM,
      angleStepDeg: LIVE_ANGLE_STEP_DEG,
    })
    const cell: PieceScale = {
      perPieceMm2: result.perPieceMm2,
      efficiency: result.efficiency,
      k: result.k,
      angleDeg: result.angleDeg,
      u: result.u,
      v: result.v,
      mateOffsetMm: result.mateOffsetMm,
    }
    cache.scale.set(key, cell)
    return cell
  } catch {
    // A degenerate ring (a trace that produced a sliver) is the only way here.
    cache.scale.set(key, null)
    return null
  }
}

function stripFor(
  indexed: readonly IndexedPiece[],
  gapMm: number,
  instanceCount: number,
  cache: EstimateCache,
): StripOutcome {
  if (instanceCount === 0) return { consumptionDesi: null, problem: null, layout: null }
  if (instanceCount > LIVE_MAX_INSTANCES) {
    return {
      consumptionDesi: null,
      problem: `Bu malzemede ${instanceCount} parça var — tüketim tahmini atlandı.`,
      layout: null,
    }
  }

  const pieces = indexed.map(({ piece }) => piece)
  const rotationsDeg = stripRotationsFor(pieces)
  const key = placementKey(pieces, gapMm, rotationsDeg)
  const cached = cache.strip.get(key)
  if (cached) return cached

  const ordered = orderedForStrip(indexed)
  const instances: Part[] = []
  for (const { piece } of ordered) instances.push(...instancesOf(piece))

  let outcome: StripOutcome
  try {
    const placed = stripPlaceBest(
      instances,
      {
        widthMm: LIVE_STRIP_WIDTH_MM,
        gapMm,
        cellMm: LIVE_CELL_MM,
        rotationsDeg,
        repeats: LIVE_REPEATS,
      },
      LIVE_STRIP_ORDERINGS,
    )
    outcome = {
      consumptionDesi: mm2ToDesi(placed.grossAreaMm2 / LIVE_REPEATS),
      problem: null,
      layout: {
        usedLengthMm: placed.usedLengthMm,
        efficiency: placed.efficiency,
        placements: placed.placements.map((placement) => ({
          xMm: placement.xMm,
          yMm: placement.yMm,
          rotDeg: placement.rotDeg,
          instance: placement.index,
        })),
      },
    }
  } catch (error: unknown) {
    // `DegenerateInstanceError` IS a `StripWidthError`, so it is tested first —
    // the two refusals mean different things to the operator: one piece is too
    // wide for the şerit, the other is too thin for the assessment grid to see
    // at all, and telling them apart is the difference between "rotate it" and
    // "the trace is wrong".
    outcome = {
      consumptionDesi: null,
      problem:
        error instanceof DegenerateInstanceError
          ? `Bir parça ${LIVE_CELL_MM} mm hesap ızgarasında görünmeyecek kadar ince — tüketim hesaplanamadı.`
          : error instanceof StripWidthError
            ? `Bir parça ${LIVE_STRIP_WIDTH_MM} mm şeride sığmıyor — tüketim hesaplanamadı.`
            : 'Tüketim hesaplanamadı.',
      layout: null,
    }
  }

  cache.strip.set(key, outcome)
  return outcome
}

/**
 * The zarf of one material: ONE PAIR nested freely by `envelopePlace`, cached
 * under the same key shape as the strip (they answer from the same inputs).
 * Failure is an ABSENT figure, never a problem string — the strip already
 * names anything worth naming, and the envelope is an additional reading, not
 * a replacement one. Same instance-count guards as the strip, for the same
 * reasons; the envelope runs the placer once per candidate width, so it is the
 * costlier of the two.
 */
function envelopeFor(
  indexed: readonly IndexedPiece[],
  gapMm: number,
  instanceCount: number,
  cache: EstimateCache,
): EnvelopePlan | null {
  if (instanceCount === 0 || instanceCount > LIVE_MAX_INSTANCES) return null

  const pieces = indexed.map(({ piece }) => piece)
  const rotationsDeg = stripRotationsFor(pieces)
  const key = placementKey(pieces, gapMm, rotationsDeg)
  const cached = cache.envelope.get(key)
  if (cached !== undefined) return cached

  const ordered = orderedForStrip(indexed)
  const instances: Part[] = []
  for (const { piece } of ordered) instances.push(...instancesOf(piece))

  let plan: EnvelopePlan | null
  try {
    const placed = envelopePlace(instances, { gapMm, cellMm: LIVE_CELL_MM, rotationsDeg })
    plan = {
      widthMm: placed.widthMm,
      lengthMm: placed.lengthMm,
      grossAreaMm2: placed.grossAreaMm2,
      efficiency: placed.efficiency,
      originMm: placed.originMm,
      placements: placed.placements.map((placement) => ({
        xMm: placement.xMm,
        yMm: placement.yMm,
        rotDeg: placement.rotDeg,
        instance: placement.index,
      })),
    }
  } catch {
    plan = null
  }

  cache.envelope.set(key, plan)
  return plan
}

/**
 * The cache key `stripFor` and `envelopeFor` share — everything a placement
 * run reads and nothing it does not. The rotation set is part of the key
 * because it is part of the answer: the same twelve shapes at the same gap lay
 * out differently once the material's direction rule closes 22 of the 24
 * angles, and a key that could not tell the two apart would hand the
 * free-rotation number back for a suede set.
 */
function placementKey(
  pieces: readonly EstimatePiece[],
  gapMm: number,
  rotationsDeg: readonly number[],
): string {
  return `${gapMm}|${rotationsDeg.join('.')}|${pieces
    .map((piece) => `${piece.geomKey}x${quantityOf(piece)}${piece.handedness}`)
    .join(',')}`
}

/**
 * The placer's feeding order — biggest first. Bottom-left placement is a
 * greedy heuristic and feeding it a small piece first buries the strip's own
 * bottom-left corner under something that could have gone in a gap later; the
 * order is fixed by area (then by key) so the answer stays a pure function of
 * the input. `stripPlaceBest` tries its own variants of this order too, but
 * this stays the as-given baseline AND the frame every placement indexes back
 * into. `layoutInstances` MUST walk the same order: it is the contract that
 * lines a cached placement up with a freshly derived instance identity.
 */
function orderedForStrip(indexed: readonly IndexedPiece[]): IndexedPiece[] {
  return [...indexed].sort(
    (a, b) =>
      b.piece.netMm2 - a.piece.netMm2 ||
      (a.piece.geomKey < b.piece.geomKey ? -1 : a.piece.geomKey > b.piece.geomKey ? 1 : 0),
  )
}

/**
 * The instance list as identities: the exact expansion `instancesOf` performs
 * for the placer — same order, same every-second-copy mirroring — but carrying
 * the request piece index and the decimated outline instead of full geometry.
 */
function layoutInstances(indexed: readonly IndexedPiece[], cache: EstimateCache): LayoutInstance[] {
  const out: LayoutInstance[] = []
  for (const { piece, index } of orderedForStrip(indexed)) {
    const quantity = quantityOf(piece)
    for (let copy = 0; copy < quantity; copy += 1) {
      const mirrored = piece.handedness === 'aynali_cift' && copy % 2 === 1
      out.push({ pieceIndex: index, mirrored, outlineMm: outlineFor(piece, mirrored, cache) })
    }
  }
  return out
}

/**
 * The decimated outer ring of a piece, memoized per `geomKey` for the life of
 * the cache — a shape is decimated once, not once per keystroke. The mirrored
 * variant is DERIVED from the decimated base (mirroring is exact, decimation
 * is not repeated) and memoized under `geomKey#m`.
 */
function outlineFor(piece: EstimatePiece, mirrored: boolean, cache: EstimateCache): Float32Array {
  const baseKey = piece.geomKey
  let base = cache.outlines.get(baseKey)
  if (!base) {
    const ring = decimateRing(piece.geom.outer)
    base = new Float32Array(ring.length * 2)
    for (let i = 0; i < ring.length; i += 1) {
      const point = ring[i] as [number, number]
      base[2 * i] = point[0]
      base[2 * i + 1] = point[1]
    }
    cache.outlines.set(baseKey, base)
  }
  if (!mirrored) return base

  const mirrorKey = `${baseKey}#m`
  const hit = cache.outlines.get(mirrorKey)
  if (hit) return hit
  // Same transform as `mirrorX`: x → −x, point order reversed so the winding
  // sign survives.
  const points = base.length / 2
  const flipped = new Float32Array(base.length)
  for (let i = 0; i < points; i += 1) {
    const j = points - 1 - i
    flipped[2 * i] = 0 - (base[2 * j] as number)
    flipped[2 * i + 1] = base[2 * j + 1] as number
  }
  cache.outlines.set(mirrorKey, flipped)
  return flipped
}

/**
 * The instances one pair needs of this piece.
 *
 * A mirrored pair is TWO different shapes on the material — that is what
 * `aynali_cift` means — so every second instance is mirrored. Placing two
 * copies of the same shape instead would quietly flatter a napped leather
 * layout, which is the one mistake this module exists to prevent.
 */
function instancesOf(piece: EstimatePiece): Part[] {
  const quantity = quantityOf(piece)
  const base = partOf(piece.geom)
  const mirrored: Part =
    piece.handedness === 'aynali_cift'
      ? { outer: mirrorX(piece.geom.outer), holes: piece.geom.holes.map((hole) => mirrorX(hole)) }
      : base

  const instances: Part[] = []
  for (let index = 0; index < quantity; index += 1) {
    instances.push(index % 2 === 1 ? mirrored : base)
  }
  return instances
}

function partOf(geom: PartGeom): Part {
  return { outer: geom.outer, holes: geom.holes }
}

/** Quantities come from a text box; the assessment gets a sane integer. */
function quantityOf(piece: EstimatePiece): number {
  const quantity = Math.trunc(piece.qtyPerPair)
  if (!Number.isFinite(quantity) || quantity < 1) return 1
  return Math.min(quantity, 99)
}

function netDesiOf(piece: EstimatePiece): number {
  return mm2ToDesi(piece.netMm2 * quantityOf(piece))
}
